import { Brand, hasMergeConflicts } from '@principle-theorem/principle-core';
import {
  type ISterilisationEquipment,
  type IBrand,
  type SterilisableItem,
} from '@principle-theorem/principle-core/interfaces';
import {
  DestinationEntityRecordStatus,
  ISourceEntityTypes,
  type FailedDestinationEntityRecord,
  type ICustomMappingHandler,
  type IDestinationEntity,
  type IDestinationEntityJobRunOptions,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IHasSourceIdentifier,
  type IMigratedDataSummary,
  type IPracticeMigration,
  type ISourceEntityHandler,
  type ISourceEntityRecord,
  type ITranslationMap,
  type MergeConflictDestinationEntityRecord,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  DocumentReference,
  Firestore,
  Timestamp,
  WithRef,
  snapshotCombineLatest,
  toTimestamp,
  FirestoreMigrate,
  PrimaryKey,
  WithPrimaryKey,
} from '@principle-theorem/shared';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PracticeMigration } from '../../practice-migrations';
import { TranslationMapHandler } from '../../translation-map';
import { BaseDestinationEntity } from '../base-destination-entity';
import { DestinationEntity } from '../destination-entity';
import { STERILISATION_EQUIPMENT_RESOURCE_TYPE } from '../../mappings/sterilisation-equipment';

export const STERILISATION_EQUIPMENT_DESTINATION_ENTITY =
  DestinationEntity.init({
    metadata: {
      key: STERILISATION_EQUIPMENT_RESOURCE_TYPE,
      label: 'Sterilisation Equipment',
      description: ``,
    },
  });

export interface ISterilisationEquipmentJobData<
  SterilisationEquipmentRecord extends ISourceEntityTypes,
> {
  sourceSterilisationEquipment: IGetRecordResponse<SterilisationEquipmentRecord>;
  brand: WithRef<IBrand>;
  sterilisationEquipmentMappings: WithPrimaryKey<
    ITranslationMap<SterilisableItem>
  >[];
}

export interface ISterilisationEquipmentDestinationRecord {
  sourceRef: PrimaryKey<ISourceEntityRecord>;
  sterilisationEquipmentRef: DocumentReference<ISterilisationEquipment>;
}

export interface ISterilisationEquipmentRecordMigrationData {
  sterilisationEquipmentRecord: ISterilisationEquipment &
    IHasSourceIdentifier & { createdAt: Timestamp };
}

export abstract class BaseSterilistionEquipmentDestinationEntity<
  SterilisationEquipmentRecord extends ISourceEntityTypes,
> extends BaseDestinationEntity<
  ISterilisationEquipmentDestinationRecord,
  ISterilisationEquipmentJobData<SterilisationEquipmentRecord>,
  ISterilisationEquipmentRecordMigrationData
> {
  destinationEntity = STERILISATION_EQUIPMENT_DESTINATION_ENTITY;
  abstract sterilisationEquipmentSourceEntity: ISourceEntityHandler<SterilisationEquipmentRecord>;
  abstract sterilisationEquipmentCustomMapping: ICustomMappingHandler<SterilisableItem>;

  get sourceCountComparison(): ISourceEntityHandler<SterilisationEquipmentRecord> {
    return this.sterilisationEquipmentSourceEntity;
  }

  sourceCountDataAccessor(
    data: ISterilisationEquipmentJobData<SterilisationEquipmentRecord>
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourceSterilisationEquipment.record.id;
  }

  getDestinationEntityRecordUid(
    data: ISterilisationEquipmentJobData<SterilisationEquipmentRecord>
  ): string {
    return data.sourceSterilisationEquipment.record.uid;
  }

  getMigratedData$(
    record: IDestinationEntityRecord<ISterilisationEquipmentDestinationRecord>
  ): Observable<IMigratedDataSummary[]> {
    if (record.status !== DestinationEntityRecordStatus.Migrated) {
      return of([]);
    }

    return Firestore.doc$(record.data.sterilisationEquipmentRef).pipe(
      map((sterilisationPack) => [
        {
          label: 'Sterilisation Pack',
          data: sterilisationPack,
        },
      ])
    );
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: ISterilisationEquipmentJobData<SterilisationEquipmentRecord>,
    _migrationData: ISterilisationEquipmentRecordMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourceSterilisationEquipment.record.uid,
      label: jobData.sourceSterilisationEquipment.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourceSterilisationEquipment.record.id,
    };
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: ISterilisationEquipmentRecordMigrationData
  ): Promise<ISterilisationEquipmentRecordMigrationData | undefined> {
    const existingSteriliserEquipmentRef =
      await translationMap.getDestination<ISterilisationEquipment>(
        data.sterilisationEquipmentRecord.sourceIdentifier,
        STERILISATION_EQUIPMENT_RESOURCE_TYPE
      );

    if (!existingSteriliserEquipmentRef) {
      return;
    }

    try {
      const existingSteriliserEquipment = await Firestore.getDoc(
        existingSteriliserEquipmentRef
      );
      const hasMergeConflict = hasMergeConflicts(
        data.sterilisationEquipmentRecord,
        existingSteriliserEquipment
      );
      if (hasMergeConflict) {
        return {
          ...data,
          sterilisationEquipmentRecord: {
            ...existingSteriliserEquipment,
            sourceIdentifier:
              data.sterilisationEquipmentRecord.sourceIdentifier,
          },
        };
      }
    } catch {
      return;
    }
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<
    ISterilisationEquipmentJobData<SterilisationEquipmentRecord>[]
  > {
    const brand$ = PracticeMigration.brand$(migration);
    const sterilisationEquipmentMappings$ =
      this.sterilisationEquipmentCustomMapping.getRecords(translationMap);

    return snapshotCombineLatest([brand$, sterilisationEquipmentMappings$])
      .pipe(
        switchMap((dependencies) =>
          combineLatest([
            this.buildSourceRecordQuery$(
              migration,
              this.sterilisationEquipmentSourceEntity,
              runOptions
            ),
            of(dependencies),
          ])
        )
      )
      .pipe(
        map(
          ([
            sourceSterilisationEquipments,
            [brand, sterilisationEquipmentMappings],
          ]) =>
            sourceSterilisationEquipments.map(
              (sourceSterilisationEquipment) => ({
                sourceSterilisationEquipment,
                brand,
                sterilisationEquipmentMappings,
              })
            )
        )
      );
  }

  async runJob(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    jobData: ISterilisationEquipmentJobData<SterilisationEquipmentRecord>,
    migrationData: ISterilisationEquipmentRecordMigrationData
  ): Promise<IDestinationEntityRecord> {
    const packCustomMapOverride = jobData.sterilisationEquipmentMappings.find(
      (packMapping) =>
        packMapping.sourceIdentifier ===
        this.sterilisationEquipmentSourceEntity
          .getSourceRecordId(jobData.sourceSterilisationEquipment.data)
          .toString()
    );

    if (packCustomMapOverride?.destinationIdentifier) {
      const sterilisationEquipmentRef =
        packCustomMapOverride.destinationIdentifier;
      await translationMap.upsert({
        sourceIdentifier:
          migrationData.sterilisationEquipmentRecord.sourceIdentifier,
        destinationIdentifier: sterilisationEquipmentRef,
        resourceType: STERILISATION_EQUIPMENT_RESOURCE_TYPE,
      });
      return this.buildSkippedResponse(jobData);
    }

    const existingPackRef =
      await translationMap.getDestination<ISterilisationEquipment>(
        migrationData.sterilisationEquipmentRecord.sourceIdentifier,
        STERILISATION_EQUIPMENT_RESOURCE_TYPE
      );
    const sterilisationEquipmentRef = await FirestoreMigrate.upsertDoc(
      Brand.sterilisationEquipmentCol(jobData.brand),
      {
        ...migrationData.sterilisationEquipmentRecord,
      },
      existingPackRef?.id
    );

    if (!existingPackRef) {
      await translationMap.upsert({
        sourceIdentifier:
          migrationData.sterilisationEquipmentRecord.sourceIdentifier,
        destinationIdentifier: sterilisationEquipmentRef,
        resourceType: STERILISATION_EQUIPMENT_RESOURCE_TYPE,
      });
    }

    return this._buildSuccessResponse(
      jobData.sourceSterilisationEquipment,
      sterilisationEquipmentRef
    );
  }

  protected _buildErrorResponse(
    sterilisationEquipment: IGetRecordResponse<SterilisationEquipmentRecord>,
    errorMessage: string = 'Failed to migrate sterilisation pack'
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: sterilisationEquipment.record.uid,
      label: sterilisationEquipment.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: sterilisationEquipment.record.id,
      errorMessage,
      failData: {},
    };
  }

  protected _buildSuccessResponse(
    record: IGetRecordResponse<SterilisationEquipmentRecord>,
    sterilisationEquipmentRef: DocumentReference<ISterilisationEquipment>
  ): IDestinationEntityRecord<ISterilisationEquipmentDestinationRecord> {
    return {
      uid: record.record.uid,
      label: record.record.label,
      data: {
        sourceRef: record.record.id,
        sterilisationEquipmentRef,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: record.record.id,
      migratedAt: toTimestamp(),
    };
  }
}
